import type { ReactNode } from 'react';
import { defineMessages } from 'react-intl-next';

const i18n = defineMessages({
	justNow: {
		id: 'content-types-header.last-edited.justNow',
		description: 'Time string in last edited that indicates last change was just now',
		defaultMessage: 'just now',
	},
	aMomentAgo: {
		id: 'content-types-header.last-edited.aMomentAgo',
		description: 'Time string in last edited that indicates last change was a moment ago',
		defaultMessage: 'a moment ago',
	},
	oneMinuteAgo: {
		id: 'content-types-header.last-edited.oneMinuteAgo',
		description: 'Time string in last edited that indicates last change was one minute ago',
		defaultMessage: '1 minute ago',
	},
	xMinutesAgo: {
		id: 'content-types-header.last-edited.xMinutesAgo',
		description: 'Time string in last edited that indicates last change was several minutes ago',
		defaultMessage: '{timeAgo} minutes ago',
	},
	oneHourAgo: {
		id: 'content-types-header.last-edited.oneHourAgo',
		description: 'Time string in last edited that indicates last change was one hour ago',
		defaultMessage: '1 hour ago',
	},
	xHoursAgo: {
		id: 'content-types-header.last-edited.xHoursAgo',
		description: 'Time string in last edited that indicates last change was several hours ago',
		defaultMessage: '{timeAgo} hours ago',
	},
	yesterday: {
		id: 'content-types-header.last-edited.yesterday',
		description: 'Time string in last edited that indicates last change was yesterday',
		defaultMessage: 'yesterday',
	},
});

const MILLISECONDS_TO_SECONDS = 1000;
const MILLISECONDS_TO_MINUTES = 60 * 1000;
const MILLISECONDS_TO_HOUR = 60 * 60 * 1000;

/**
 * Takes lastEditedTime as Date object and converts to relative friendly timestamp format.
 */
export const lastEditedFriendlyFormatter = (
	lastEditedTime: Date,
	formatMessage: any,
	formatDate: any,
) => {
	let formattedDate: ReactNode;
	const timeNow = new Date();
	const timeDifference = timeNow.getTime() - lastEditedTime.getTime();

	if (timeDifference / MILLISECONDS_TO_SECONDS < 4) {
		formattedDate = formatMessage(i18n.justNow);
	} else if (timeDifference / MILLISECONDS_TO_MINUTES < 1) {
		formattedDate = formatMessage(i18n.aMomentAgo);
	} else if (Math.floor(timeDifference / MILLISECONDS_TO_MINUTES) === 1) {
		formattedDate = formatMessage(i18n.oneMinuteAgo);
	} else if (timeDifference / MILLISECONDS_TO_MINUTES < 60) {
		const minutes = Math.floor(timeDifference / MILLISECONDS_TO_MINUTES);
		formattedDate = formatMessage(i18n.xMinutesAgo, { timeAgo: minutes });
	} else if (Math.floor(timeDifference / MILLISECONDS_TO_HOUR) === 1) {
		formattedDate = formatMessage(i18n.oneHourAgo);
	} else if (timeDifference / MILLISECONDS_TO_HOUR < 24) {
		const hours = Math.floor(timeDifference / MILLISECONDS_TO_HOUR);
		formattedDate = formatMessage(i18n.xHoursAgo, { timeAgo: hours });
	} else if (timeDifference / MILLISECONDS_TO_HOUR < 48) {
		formattedDate = formatMessage(i18n.yesterday);
	} else {
		// After 48 hours, include date in format MMM DD, YYYY (shortened month 'Jan')
		formattedDate = formatDate(lastEditedTime, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});

		return { formattedDate, isLongFormat: true };
	}

	return { formattedDate, isLongFormat: false };
};
